// UNIVAPLAT-1904: WhatsApp-style conversation panel — right column of the admin messaging page.
// Owns its own data: fetches the unified inbound+outbound conversation, polls for new inbound
// replies while the tab is visible, and supports loading older pages + a free-text send box.
import {AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {AdminWhatsappService} from '@shared/api/services/v2/admin-whatsapp.service';
import {WhatsAppConversationItem, WhatsAppConversationResponse} from '@shared/api/models/query/admin-whatsapp.model';

type WhatsAppMediaRef = {url: string; contentType: string};

export const WHATSAPP_MESSAGE_MAX_LENGTH = 1600;
export const WHATSAPP_POLL_INTERVAL_MS = 10_000;
const SCROLL_BOTTOM_THRESHOLD_PX = 100;

@Component({
    selector: 'cdss-admin-whatsapp-conversation',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './admin-whatsapp-conversation.component.html',
    styleUrls: ['./admin-whatsapp-conversation.component.scss'],
})
export class AdminWhatsappConversationComponent implements OnInit, AfterViewChecked, OnDestroy {
    @Input() userId!: number;

    /** Emitted after a successful send so the parent page can reload the table's data. */
    @Output() messageSent = new EventEmitter<void>();

    @ViewChild('scrollAnchor') private scrollAnchor?: ElementRef<HTMLDivElement>;
    @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLDivElement>;

    readonly maxLength = WHATSAPP_MESSAGE_MAX_LENGTH;
    messageControl = new FormControl('', {nonNullable: true, validators: [Validators.maxLength(WHATSAPP_MESSAGE_MAX_LENGTH)]});

    items: WhatsAppConversationItem[] = [];
    hasMore = false;
    private nextBefore?: string;
    /**
     * True once loadOlder() has prepended a page, i.e. the loaded window extends beyond the
     * newest page. Controls whether a newest-page response may replace the list wholesale —
     * see applyNewestPage() for the rule.
     */
    private historyExtended = false;
    /**
     * Single-flight guard for the 10s poll. setInterval fires regardless of whether the
     * previous tick settled, so without this a response slower than the interval would stack
     * concurrent getConversation calls and a stale response could overwrite a fresher list
     * (last-writer-wins on `items`).
     */
    private pollInFlight = false;

    loading = true;
    loadingOlder = false;
    sending = false;
    windowClosed = false;
    errorMessage: string | null = null;

    private shouldScrollToBottom = false;
    private preserveScrollAnchorEl: HTMLElement | null = null;
    private preserveScrollOffset = 0;

    private pollHandle?: ReturnType<typeof setInterval>;
    private readonly visibilityChangeHandler = () => this.onVisibilityChange();

    // WhatsApp media display design (2026-07-30, §6): object-URL cache keyed by media path.
    // Load-bearing, not an optimisation — the poll replaces `items` wholesale every 10s, so
    // without this cache every attachment would re-download six times a minute.
    private mediaCache = new Map<string, {objectUrl: string; safeUrl: SafeUrl}>();
    private mediaLoading = new Set<string>();
    private mediaFailed = new Set<string>();
    private destroyed = false;

    /** Currently previewed attachment, plus the media path it came from. Null when the overlay is closed. */
    lightboxUrl: SafeUrl | null = null;
    lightboxIsVideo = false;
    private lightboxPath: string | null = null;

    constructor(private adminWhatsappService: AdminWhatsappService, private sanitizer: DomSanitizer) {}

    ngOnInit(): void {
        this.loadInitial();
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        this.startPolling();
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
        if (this.preserveScrollAnchorEl) {
            const container = this.messagesContainer?.nativeElement;
            if (container) {
                container.scrollTop = this.preserveScrollAnchorEl.offsetTop - this.preserveScrollOffset;
            }
            this.preserveScrollAnchorEl = null;
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        this.stopPolling();
        this.revokeAllMedia();
    }

    get isSendDisabled(): boolean {
        const trimmed = this.messageControl.value.trim();
        return this.sending || trimmed.length === 0 || trimmed.length > this.maxLength;
    }

    async loadInitial(): Promise<void> {
        this.errorMessage = null;
        this.loading = true;
        try {
            const response = await this.adminWhatsappService.getConversation(this.userId);
            if (this.destroyed) {
                return; // component torn down while the request was in flight
            }
            this.items = response.items ?? [];
            this.hasMore = response.hasMore ?? false;
            this.nextBefore = response.nextBefore;
            this.historyExtended = false;
            this.syncMedia();
            this.shouldScrollToBottom = true;
        } catch (err: any) {
            this.errorMessage = err?.error?.message ?? err?.error?.detail ?? 'Failed to load conversation.';
        } finally {
            this.loading = false;
        }
    }

    async loadOlder(): Promise<void> {
        if (this.loadingOlder || !this.hasMore) {
            return;
        }
        this.loadingOlder = true;
        this.errorMessage = null;
        const previousFirstEl = this.messagesContainer?.nativeElement.querySelector<HTMLElement>('[data-conversation-item]') ?? null;
        const container = this.messagesContainer?.nativeElement;
        const previousFirstOffset = previousFirstEl && container ? previousFirstEl.offsetTop - container.scrollTop : 0;
        try {
            const response = await this.adminWhatsappService.getConversation(this.userId, this.nextBefore);
            if (this.destroyed) {
                return; // component torn down while the request was in flight
            }
            const older = response.items ?? [];
            this.items = [...older, ...this.items];
            if (older.length > 0) {
                this.historyExtended = true;
            }
            this.hasMore = response.hasMore ?? false;
            this.nextBefore = response.nextBefore;
            this.syncMedia();
            if (previousFirstEl) {
                this.preserveScrollAnchorEl = previousFirstEl;
                this.preserveScrollOffset = previousFirstOffset;
            }
        } catch (err: any) {
            this.errorMessage = err?.error?.message ?? err?.error?.detail ?? 'Failed to load earlier messages.';
        } finally {
            this.loadingOlder = false;
        }
    }

    async send(): Promise<void> {
        const trimmed = this.messageControl.value.trim();
        if (this.isSendDisabled || trimmed.length === 0) {
            return;
        }
        this.sending = true;
        this.errorMessage = null;
        this.windowClosed = false;
        try {
            await this.adminWhatsappService.sendFreeTextMessage(this.userId, trimmed);
            this.messageControl.setValue('');
            this.messageSent.emit();
            await this.refetchAfterSend();
        } catch (err: any) {
            // Never surface err?.message directly — that can expose the API URL from HttpErrorResponse.
            if (err?.status === 422 && err?.error?.code === 'whatsapp_outside_session_window') {
                this.windowClosed = true;
            } else {
                this.errorMessage = err?.error?.message ?? err?.error?.detail ?? 'Failed to send message.';
            }
        } finally {
            this.sending = false;
        }
    }

    dismissWindowClosedNotice(): void {
        this.windowClosed = false;
    }

    dismissError(): void {
        this.errorMessage = null;
    }

    // ─── Media rendering (WhatsApp media display design 2026-07-30, §6) ──────────

    /** Blob object URL (sanitizer-bypassed) for a media entry, or null while loading / failed. */
    mediaSafeUrl(media: WhatsAppMediaRef): SafeUrl | null {
        return this.mediaCache.get(media.url)?.safeUrl ?? null;
    }

    isMediaImage(media: WhatsAppMediaRef): boolean {
        return media.contentType.startsWith('image/');
    }

    isMediaAudio(media: WhatsAppMediaRef): boolean {
        return media.contentType.startsWith('audio/');
    }

    isMediaVideo(media: WhatsAppMediaRef): boolean {
        return media.contentType.startsWith('video/');
    }

    /**
     * Full-screen preview for images and video. Both open in an in-app overlay rather than a
     * new tab: the blob object URL is scoped to this document, so a new tab would be a second
     * consumer of a URL this component revokes on poll/destroy.
     *
     * Images open on clicking the thumbnail. Video opens from a dedicated expand control —
     * clicking the video element itself must stay with the native play/pause behaviour.
     */
    openLightbox(media: WhatsAppMediaRef): void {
        const safeUrl = this.mediaSafeUrl(media);
        if (!safeUrl) {
            return;
        }
        this.lightboxUrl = safeUrl;
        this.lightboxIsVideo = this.isMediaVideo(media);
        this.lightboxPath = media.url;
    }

    closeLightbox(): void {
        this.lightboxUrl = null;
        this.lightboxIsVideo = false;
        this.lightboxPath = null;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closeLightbox();
    }

    /** True once the blob fetch for this media entry has failed (e.g. Twilio deleted it). */
    isMediaUnavailable(media: WhatsAppMediaRef): boolean {
        return this.mediaFailed.has(media.url);
    }

    /** Chip shown while the blob is in flight, or as the unavailable state after a failed fetch. */
    mediaChipLabel(media: WhatsAppMediaRef): string {
        return this.isMediaUnavailable(media)
            ? `[attachment unavailable: ${media.contentType}]`
            : `[attachment: ${media.contentType}]`;
    }

    outboundLabel(item: WhatsAppConversationItem): string {
        return item.templateName ? `Template: ${item.templateName}` : (item.text ?? '');
    }

    /**
     * Identity for the bubble list. The 10s poll swaps in a fresh `items` array, so without a
     * stable key Angular tears down and rebuilds every row, resetting in-progress audio/video
     * playback (and losing the user's scroll position within a media element).
     */
    trackByItemId = (_: number, item: WhatsAppConversationItem): string => item.id;

    /**
     * Identity for the per-bubble attachment loop. Needed for the same reason as
     * trackByItemId: the poll hands back fresh media objects, and this is the loop that
     * directly wraps the <video>/<audio> element. The proxy path is stable per attachment.
     */
    trackByMediaUrl = (_: number, media: WhatsAppMediaRef): string => media.url;

    private async refetchAfterSend(): Promise<void> {
        try {
            const response = await this.adminWhatsappService.getConversation(this.userId);
            if (this.destroyed) {
                return; // component torn down while the request was in flight
            }
            this.applyNewestPage(response);
            this.shouldScrollToBottom = true;
        } catch {
            // UNIVAPLAT-1904 §3.2: only user-initiated loads surface errors; a refetch-after-send
            // failure here is silent — the send itself already succeeded and was reported above.
        }
    }

    /**
     * Apply a newest-page response (10s poll or refetch-after-send) to the loaded list.
     *
     * Rule: conversation design §3.2 (wholesale list replacement) applies ONLY while the loaded
     * window is exactly the newest page. Once loadOlder() (§3.4) has extended the window,
     * §3.3 wins — a poll must never yank the admin away from reading history — so instead of
     * replacing the list:
     *   - items already loaded are refreshed in place (picks up outbound status updates),
     *   - only genuinely new items (unknown ids) are appended at the end,
     *   - hasMore / nextBefore are left untouched: they describe the OLDEST loaded boundary,
     *     and the newest page's cursor would reset paging over already-loaded history.
     * Either way syncMedia() then reconciles the object-URL cache without revoking attachments
     * that are still on screen.
     */
    private applyNewestPage(response: WhatsAppConversationResponse): void {
        const incoming = response.items ?? [];
        if (!this.historyExtended) {
            this.items = incoming;
            this.hasMore = response.hasMore ?? false;
            this.nextBefore = response.nextBefore;
        } else {
            const freshById = new Map(incoming.map(item => [item.id, item] as const));
            const knownIds = new Set(this.items.map(item => item.id));
            this.items = [
                ...this.items.map(item => freshById.get(item.id) ?? item),
                ...incoming.filter(item => !knownIds.has(item.id)),
            ];
        }
        this.syncMedia();
    }

    private startPolling(): void {
        this.stopPolling();
        this.pollHandle = setInterval(() => this.poll(), WHATSAPP_POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.pollHandle) {
            clearInterval(this.pollHandle);
            this.pollHandle = undefined;
        }
    }

    private onVisibilityChange(): void {
        if (document.visibilityState === 'visible') {
            this.startPolling();
        } else {
            this.stopPolling();
        }
    }

    private async poll(): Promise<void> {
        if (document.visibilityState !== 'visible') {
            return;
        }
        // Single-flight: skip this tick while a previous poll is still awaiting (slow response)
        // or while a load-older / send-refetch currently owns the item list — otherwise the
        // requests race and the last writer wins regardless of freshness.
        if (this.pollInFlight || this.loadingOlder || this.sending) {
            return;
        }
        this.pollInFlight = true;
        const wasNearBottom = this.isNearBottom();
        try {
            const response = await this.adminWhatsappService.getConversation(this.userId);
            if (this.destroyed) {
                return; // component torn down while the request was in flight
            }
            this.applyNewestPage(response);
            if (wasNearBottom) {
                this.shouldScrollToBottom = true;
            }
        } catch {
            // UNIVAPLAT-1904 §3.2 / §4: poll failures are silent — never churn the error banner.
        } finally {
            this.pollInFlight = false;
        }
    }

    private isNearBottom(): boolean {
        const container = this.messagesContainer?.nativeElement;
        if (!container) {
            return true;
        }
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        return distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
    }

    private scrollToBottom(): void {
        try {
            this.scrollAnchor?.nativeElement.scrollIntoView({block: 'end'});
        } catch {
            /* no-op — best-effort scroll */
        }
    }

    // ─── Media cache lifecycle (WhatsApp media display design 2026-07-30, §6) ───

    /**
     * Reconcile the object-URL cache with the current item list. Called after every wholesale
     * `items` replacement (initial load, load-older, refetch-after-send, 10s poll):
     * fetch only paths not already cached/in-flight/failed, and revoke object URLs for paths
     * that are no longer present. Failed paths are NOT retried on later polls — Twilio may have
     * deleted the media, and retrying every 10s would defeat the point of the cache.
     */
    private syncMedia(): void {
        const currentPaths = new Set<string>();
        for (const item of this.items) {
            for (const media of item.media ?? []) {
                currentPaths.add(media.url);
            }
        }

        for (const [path, entry] of Array.from(this.mediaCache.entries())) {
            if (!currentPaths.has(path)) {
                URL.revokeObjectURL(entry.objectUrl);
                this.mediaCache.delete(path);
                // The overlay holds this object URL; revoking it underneath would leave a
                // broken preview, so close it instead.
                if (this.lightboxPath === path) {
                    this.closeLightbox();
                }
            }
        }
        for (const path of Array.from(this.mediaFailed)) {
            if (!currentPaths.has(path)) {
                this.mediaFailed.delete(path);
            }
        }

        for (const path of currentPaths) {
            if (this.mediaCache.has(path) || this.mediaLoading.has(path) || this.mediaFailed.has(path)) {
                continue;
            }
            this.fetchMedia(path);
        }
    }

    private async fetchMedia(path: string): Promise<void> {
        this.mediaLoading.add(path);
        try {
            const blob = await this.adminWhatsappService.getMediaBlob(path);
            const objectUrl = URL.createObjectURL(blob);
            if (this.destroyed) {
                // Fetch settled after teardown — don't leak the object URL.
                URL.revokeObjectURL(objectUrl);
                return;
            }
            this.mediaCache.set(path, {objectUrl, safeUrl: this.sanitizer.bypassSecurityTrustUrl(objectUrl)});
        } catch {
            // Design §6: the chip stays and shows an unavailable state rather than a broken image.
            this.mediaFailed.add(path);
        } finally {
            this.mediaLoading.delete(path);
        }
    }

    private revokeAllMedia(): void {
        for (const entry of this.mediaCache.values()) {
            URL.revokeObjectURL(entry.objectUrl);
        }
        this.mediaCache.clear();
        this.mediaLoading.clear();
        this.mediaFailed.clear();
        this.closeLightbox();
    }
}
